import React from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import color from "../../../Constants/colors";
import { getModelIcon } from "../../../Helpers/modelHelpers";
import {
  DocumentCraftingModel,
  DocumentCraftingModelsResponse,
} from "../../../Models/documentCrafting";
import { useDocumentCraftingModels } from "../../../Query/useDocumentCrafting";

/**
 * The model a form uses: the user's explicit choice, else the preferred one
 * (e.g. the base revision's model) if it is still accessible, else the
 * module default. The form submits exactly this, so it matches what
 * `ModelSelect` shows.
 */
export const resolveDocumentCraftingModelId = (
  models: DocumentCraftingModelsResponse | undefined,
  value: number | null | undefined,
  preferredModelId?: number | null,
): number | null => {
  if (!models) return value ?? null;
  const accessible = (id: number | null | undefined) =>
    id !== null &&
    id !== undefined &&
    models.models.some((model) => model.model_id === id);
  if (accessible(value)) return value as number;
  if (accessible(preferredModelId)) return preferredModelId as number;
  if (accessible(models.default_model_id)) return models.default_model_id;
  return models.models[0]?.model_id ?? null;
};

const ModelOption: React.FC<{ model: DocumentCraftingModel }> = ({ model }) => {
  const icon = getModelIcon(model.model_name);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      {icon ? (
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{ width: 18, height: 18, objectFit: "contain", flexShrink: 0 }}
        />
      ) : (
        <Box sx={{ width: 18, flexShrink: 0 }} />
      )}
      <Typography
        component="span"
        noWrap
        sx={{ fontSize: 14, color: color.gray900 }}
      >
        {model.model_name}
      </Typography>
    </Box>
  );
};

interface Props {
  /** The explicit choice; null shows (and means) the resolved default. */
  value: number | null;
  onChange: (modelId: number) => void;
  /** E.g. the base revision's model for a new revision. */
  preferredModelId?: number | null;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Hide the fallback hints (e.g. in a compact dialog). */
  hideHints?: boolean;
}

/**
 * Model picker of the module, fed by `GET /models/` (only the models the user
 * may use). Shows a hint when the module default (Claude Sonnet 5.5) or the
 * preferred model is not available and another one is preselected.
 */
const ModelSelect: React.FC<Props> = ({
  value,
  onChange,
  preferredModelId,
  label,
  disabled,
  error,
  helperText,
  hideHints,
}) => {
  const { t } = useTranslation("documentCrafting");
  const models = useDocumentCraftingModels();
  const data = models.data;
  const selected = resolveDocumentCraftingModelId(data, value, preferredModelId);
  const selectedModel = data?.models.find((model) => model.model_id === selected);

  if (models.isLoading) {
    return <Skeleton variant="rounded" height={40} />;
  }
  if (!data || !data.models.length) {
    return (
      <Alert severity="warning">
        {models.isError ? t("common.models.loadError") : t("common.models.none")}
      </Alert>
    );
  }

  const preferredMissing =
    value === null &&
    preferredModelId !== null &&
    preferredModelId !== undefined &&
    selected !== preferredModelId;
  const defaultIsFallback =
    !preferredMissing &&
    data.default_is_fallback &&
    selected === data.default_model_id;

  return (
    <Box>
      <TextField
        select
        fullWidth
        size="small"
        label={label ?? t("common.models.label")}
        value={selected ?? ""}
        onChange={(event) => onChange(Number(event.target.value))}
        disabled={disabled}
        error={error}
        helperText={helperText}
        SelectProps={{
          renderValue: () =>
            selectedModel ? <ModelOption model={selectedModel} /> : "",
        }}
      >
        {data.models.map((model) => (
          <MenuItem key={model.model_id} value={model.model_id}>
            <ModelOption model={model} />
            {model.model_id === data.default_model_id && (
              <Typography
                component="span"
                sx={{ ml: "auto", pl: 2, fontSize: 12, color: color.gray500 }}
              >
                {t("common.models.default")}
              </Typography>
            )}
          </MenuItem>
        ))}
      </TextField>
      {!hideHints && preferredMissing && selectedModel && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t("common.models.preferredUnavailable", {
            model: selectedModel.model_name,
          })}
        </Alert>
      )}
      {!hideHints && defaultIsFallback && selectedModel && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t("common.models.fallbackHint", { model: selectedModel.model_name })}
        </Alert>
      )}
    </Box>
  );
};

export default ModelSelect;
